import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import MapParcelPicker from "./MapParcelPicker";
import gminyData from "./gminy.json";

const API_KEY =
  "88fc34912a9b6e430657880fb073992ad2e43de494a6ff9673bc14c12caf09ff";

/* ════════════════════════════════════════════════════════════════
   ULDK — pomocnicze (lista obrębów gminy + rozbiór TERYT-u działki)
   ════════════════════════════════════════════════════════════════ */

const ULDK_BASE = "https://uldk.gugik.gov.pl/";

async function uldkRows(params) {
  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  const res = await fetch(`${ULDK_BASE}?${qs}`);
  const text = await res.text();
  const lines = text.replace(/\r/g, "").trim().split("\n");
  return {
    status: (lines[0] || "").trim(),
    rows: lines.slice(1).filter(Boolean),
  };
}

// GetRegionById z TERYT-em GMINY (WWPPGG_R) zwraca listę WSZYSTKICH obrębów tej gminy.
async function fetchRegionsByCommune(communeTeryt) {
  if (!communeTeryt) return [];
  const { status, rows } = await uldkRows({
    request: "GetRegionById",
    id: communeTeryt,
    result: "region,teryt",
  });
  if (status !== "0") return [];
  return rows
    .map((l) => {
      const [name, teryt] = l.split("|");
      return { name: (name || "").trim(), teryt: (teryt || "").trim() };
    })
    .filter((r) => r.teryt)
    .sort((a, b) => a.name.localeCompare(b.name, "pl"));
}

// TERYT działki: WWPPGG_R.OOOO.[AR_NR.].NR_DZ  →  gmina = WWPPGG_R, obręb = WWPPGG_R.OOOO
function splitParcelTeryt(teryt) {
  if (!teryt) return { communeTeryt: "", obrebTeryt: "" };
  const p = teryt.split(".");
  return {
    communeTeryt: p[0] || "",
    obrebTeryt: p.length >= 2 ? `${p[0]}.${p[1]}` : "",
  };
}

// środek geometrii WKT (pierwszy pierścień), współrzędne w EPSG:4326 → { lat, lng }
function wktCentroid(wkt) {
  if (!wkt) return null;
  const m = wkt.match(/\(([-0-9.eE\s,]+)\)/); // pierwszy pierścień
  if (!m) return null;
  const pts = m[1]
    .split(",")
    .map((p) => p.trim().split(/\s+/).map(Number))
    .filter((c) => c.length >= 2 && !isNaN(c[0]) && !isNaN(c[1]));
  if (!pts.length) return null;
  const s = pts.reduce((a, [x, y]) => [a[0] + x, a[1] + y], [0, 0]);
  return { lng: s[0] / pts.length, lat: s[1] / pts.length };
}

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await res.json();
    return { postalCode: data?.address?.postcode || "" };
  } catch {
    return { postalCode: "" };
  }
}

// kod pocztowy dla obrębu: geometria obrębu (4326) → środek → reverse geocode
async function fetchPostalForRegion(obrebTeryt) {
  if (!obrebTeryt) return "";
  const { status, rows } = await uldkRows({
    request: "GetRegionById",
    id: obrebTeryt,
    result: "geom_wkt",
    srid: "4326",
  });
  if (status !== "0" || !rows[0]) return "";
  const c = wktCentroid(rows[0].split("|")[0]);
  if (!c) return "";
  const { postalCode } = await reverseGeocode(c.lat, c.lng);
  return postalCode;
}

/* ════════════════════════════════════════════════════════════════
   Autocomplete — input z podpowiedziami (wygląd dziedziczy z .field input)
   ════════════════════════════════════════════════════════════════ */

if (typeof document !== "undefined" && !document.getElementById("ac-style")) {
  const s = document.createElement("style");
  s.id = "ac-style";
  s.textContent = "@keyframes acspin{to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}

const AC_MENU = {
  position: "absolute",
  zIndex: 3000,
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  margin: 0,
  padding: 4,
  listStyle: "none",
  background: "#fff",
  border: "1.5px solid #d6ebeb",
  borderRadius: 14,
  boxShadow: "0 10px 28px rgba(40,90,90,.14)",
  maxHeight: 240,
  overflowY: "auto",
};
const AC_OPT = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 10,
  cursor: "pointer",
};
const AC_SPIN = {
  position: "absolute",
  right: 14,
  top: "50%",
  marginTop: -7,
  width: 14,
  height: 14,
  border: "2px solid rgba(125,212,212,.35)",
  borderTopColor: "#7dd4d4",
  borderRadius: "50%",
  animation: "acspin .7s linear infinite",
  pointerEvents: "none",
};

function Autocomplete({
  value,
  placeholder,
  disabled,
  options,
  loading,
  minChars = 1,
  onText,
  onSelect,
  emptyText = "Brak wyników",
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const q = (value || "").trim().toLowerCase();
  const filtered =
    q.length < minChars
      ? minChars === 0
        ? options.slice(0, 50)
        : []
      : options
          .filter(
            (o) =>
              o.label.toLowerCase().includes(q) ||
              (o.sub || "").toLowerCase().includes(q)
          )
          .slice(0, 50);

  const pick = (o) => {
    onSelect(o);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && open && filtered[active]) {
      e.preventDefault();
      pick(filtered[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showEmpty =
    open &&
    !disabled &&
    !loading &&
    filtered.length === 0 &&
    q.length >= minChars;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          onText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {loading && <span style={AC_SPIN} aria-hidden />}
      {open && !disabled && filtered.length > 0 && (
        <ul style={AC_MENU}>
          {filtered.map((o, i) => (
            <li
              key={o.key}
              style={{
                ...AC_OPT,
                background: i === active ? "#effafa" : "#fff",
              }}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
            >
              <span style={{ fontSize: "0.95rem", color: "#2e6b6b" }}>
                {o.label}
              </span>
              {o.sub && (
                <span style={{ fontSize: "0.72rem", color: "#9bbcbc" }}>
                  {o.sub}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {showEmpty && (
        <ul style={AC_MENU}>
          <li style={{ ...AC_OPT, cursor: "default", color: "#9bbcbc" }}>
            {emptyText}
          </li>
        </ul>
      )}
    </div>
  );
}

function App() {
  const LONG_TIMEOUT_MS = 600_000;

  const INITIAL_FORM = {
    parcelNo: "",
    municipality: "",
    obreb: "",
    postalCode: "",
    name: "",
    phone: "",
    mail: "",
    cena_m2: "",
    rodzajDzialki: "",
    numerKW: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [pp, setPP] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [error, setError] = useState(null);
  const [noParcel, setNoParcel] = useState(false);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({ mail: "", phone: "" });

  /* ── działka: słownik gmin + obręby + identyfikatory TERYT ── */
  const [gminy] = useState(() => (Array.isArray(gminyData) ? gminyData : []));
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [postalLoading, setPostalLoading] = useState(false);
  const [parcelMeta, setParcelMeta] = useState({
    communeTeryt: "",
    obrebTeryt: "",
    teryt: "",
  });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-().]/g, "");
    return /^(\+?48)?[4-9]\d{8}$|^\+?[1-9]\d{6,14}$/.test(cleaned);
  };

  const requestAbortRef = useRef(null);
  const fileInputRef = useRef(null);

  /* ── obręby — przeładuj po zmianie gminy (z mapy lub z listy) ── */
  useEffect(() => {
    const ct = parcelMeta.communeTeryt;
    if (!ct) {
      setRegions([]);
      return;
    }
    let alive = true;
    setRegionsLoading(true);
    fetchRegionsByCommune(ct)
      .then((rs) => alive && setRegions(rs))
      .catch(() => alive && setRegions([]))
      .finally(() => alive && setRegionsLoading(false));
    return () => {
      alive = false;
    };
  }, [parcelMeta.communeTeryt]);

  const gminaOptions = useMemo(
    () =>
      gminy.map((g) => ({
        key: g.teryt,
        label: g.name,
        sub: [g.county, g.voivodeship].filter(Boolean).join(", "),
        raw: g,
      })),
    [gminy]
  );
  const regionOptions = useMemo(
    () => regions.map((r) => ({ key: r.teryt, label: r.name, raw: r })),
    [regions]
  );

  /* ── offline ── */
  useEffect(() => {
    const onOffline = () => setError("Brak połączenia z internetem.");
    window.addEventListener("offline", onOffline);
    return () => window.removeEventListener("offline", onOffline);
  }, []);

  /* ── blokada scrolla przy modalach ── */
  useEffect(() => {
    document.body.style.overflow = showMapModal ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showMapModal]);

  /* ── zmiana trybu ── */
  const handleNoParcelChange = (e) => {
    const checked = e.target.checked;
    setNoParcel(checked);
    if (checked) {
      setFormData((fd) => ({
        ...fd,
        parcelNo: "",
        municipality: "",
        obreb: "",
        postalCode: "",
        cena_m2: "",
        rodzajDzialki: "",
        numerKW: "",
      }));
      setParcelMeta({ communeTeryt: "", obrebTeryt: "", teryt: "" });
      setRegions([]);
    }
    setFiles([]);
    setError(null);
  };

  const handleParcelFromMap = ({
    parcelNo,
    municipality,
    obreb,
    postalCode,
    teryt,
  }) => {
    const { communeTeryt, obrebTeryt } = splitParcelTeryt(teryt || "");
    setFormData((prev) => ({
      ...prev,
      parcelNo: parcelNo || "",
      municipality: municipality || "",
      obreb: obreb || "",
      postalCode: postalCode || "",
    }));
    setParcelMeta({ communeTeryt, obrebTeryt, teryt: teryt || "" });
    setError(null);
    setShowMapModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (name === "mail") {
      setErrors((prev) => ({
        ...prev,
        mail:
          value && !validateEmail(value) ? "Podaj poprawny adres e-mail" : "",
      }));
    }

    if (name === "phone") {
      setErrors((prev) => ({
        ...prev,
        phone:
          value && !validatePhone(value) ? "Podaj poprawny numer telefonu" : "",
      }));
    }
  };

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => {
      const all = [...prev, ...picked];
      const limited = all.slice(0, 10);
      const totalMB = limited.reduce((s, f) => s + f.size, 0) / 1024 / 1024;
      if (totalMB > 20) {
        setError("Łączny rozmiar załączników nie może przekraczać 20 MB.");
        return prev;
      }
      return limited;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  /* ── fire & forget ── */
  const fireAndForget = (fd) => {
    if (requestAbortRef.current) {
      try {
        requestAbortRef.current.abort();
      } catch {}
    }
    const controller = new AbortController();
    requestAbortRef.current = controller;
    const timer = setTimeout(() => controller.abort(), LONG_TIMEOUT_MS);

    fetch("https://apislu.kppp.pl/mapka", {
      method: "POST",
      headers: { "X-API-Key": API_KEY },
      body: fd,
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) console.warn("[mapka] server error", res.status);
        else {
          const data = await res.json().catch(() => null);
          console.log("[mapka] done", data?.parcelId);
        }
      })
      .catch((err) => {
        if (err?.name !== "AbortError")
          console.warn("[mapka] fetch error", err.message);
      })
      .finally(() => {
        clearTimeout(timer);
        if (requestAbortRef.current === controller)
          requestAbortRef.current = null;
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      mail: !validateEmail(formData.mail) ? "Podaj poprawny adres e-mail" : "",
      phone: !validatePhone(formData.phone)
        ? "Podaj poprawny numer telefonu"
        : "",
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) return; // zatrzymaj jeśli są błędy

    // ── walidacja danych osobowych (zawsze) ──
    if (!formData.name?.trim()) {
      setError("Wpisz imię i nazwisko.");
      return;
    }
    if (!formData.mail?.trim()) {
      setError("Wpisz adres e-mail.");
      return;
    }
    if (!formData.phone?.trim()) {
      setError("Wpisz numer telefonu.");
      return;
    }

    // ── walidacja danych działki ──
    if (!noParcel) {
      if (
        !formData.parcelNo?.trim() ||
        !formData.municipality?.trim() ||
        !formData.obreb?.trim()
      ) {
        setError(
          "Uzupełnij numer działki, obręb i miejscowość przed wysłaniem formularza."
        );
        return;
      }
      if (formData.cena_m2) {
        const val = Number(formData.cena_m2);
        if (isNaN(val) || val <= 0) {
          setError("Orientacyjna cena m² musi być liczbą większą od 0.");
          return;
        }
      }
    }

    const fd = new FormData();
    fd.append("name", formData.name);
    fd.append("phone", formData.phone);
    fd.append("mail", formData.mail);
    fd.append("mode", noParcel ? "contact" : "parcel");
    fd.append("half", "100");
    fd.append("noParcel", noParcel ? "1" : "0");

    if (!noParcel) {
      fd.append("municipality", formData.municipality);
      fd.append("parcelNo", formData.parcelNo);
      fd.append("obreb", formData.obreb);
      fd.append("postalCode", formData.postalCode);
      if (formData.cena_m2 && Number(formData.cena_m2) > 0)
        fd.append("cena_m2", formData.cena_m2);
      fd.append(
        "rodzajDzialki",
        formData.rodzajDzialki?.trim() || "nie podano"
      );
      fd.append("numerKW", formData.numerKW?.trim() || "nie podano");
    }

    // pliki — zawsze (i tryb parcel i contact)
    files.forEach((file) => fd.append("attachments", file, file.name));

    fireAndForget(fd);

    window.gtag?.("event", "conversion", {
      send_to: "AW-999769198/MBdUCNqlk5QcEO6I3dwD",
      value: 1.0,
      currency: "PLN",
    });

    setFormData(INITIAL_FORM);
    setParcelMeta({ communeTeryt: "", obrebTeryt: "", teryt: "" });
    setRegions([]);
    setPP(false);
    setFiles([]);
    setError(null);
    setShowSuccess(true);
    setLoading(false);
  };

  const parcelFilled =
    !!formData.parcelNo?.trim() && !!formData.municipality?.trim();

  /* ────────────────────────────── RENDER */
  return (
    <>
      <div>
        <section className="input-section">
          <form onSubmit={handleSubmit} className="glass-card">
            {/* ════ SEKCJA DZIAŁKI ════ */}
            {!noParcel && (
              <div className="parcel-section">
                <div className="map-btn-wrap" style={{ marginBottom: "20px" }}>
                  <button
                    type="button"
                    className="map-open-btn"
                    onClick={() => setShowMapModal(true)}
                    disabled={loading}
                  >
                    <svg
                      viewBox="0 0 20 20"
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M10 2C6.686 2 4 4.686 4 8c0 4.418 6 10 6 10s6-5.582 6-10c0-3.314-2.686-6-6-6z" />
                      <circle cx="10" cy="8" r="2" />
                    </svg>
                    Wskaż działkę na mapie
                  </button>
                </div>

                <div className="input-row">
                  <div className="field">
                    <div style={{ display: "flex", gap: "10px" }}>
                      <img
                        src="https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_1.png"
                        className="imgLabel"
                      />
                      <span className="spanLabel">Nr działki *</span>
                    </div>
                    <input
                      name="parcelNo"
                      value={formData.parcelNo}
                      onChange={handleChange}
                      placeholder="np. 123/4"
                      disabled={loading}
                    />
                  </div>
                  <div className="field">
                    <div style={{ display: "flex", gap: "10px" }}>
                      <img
                        src="https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_2.png"
                        className="imgLabel"
                      />
                      <span className="spanLabel">Gmina *</span>
                    </div>
                    {/* ZMIANA: pole z wyszukiwaniem gmin (słownik) */}
                    <Autocomplete
                      value={formData.municipality}
                      placeholder="np. Wrocław"
                      disabled={loading}
                      options={gminaOptions}
                      minChars={2}
                      emptyText="Nie znaleziono gminy"
                      onText={(text) => {
                        setFormData((prev) => ({
                          ...prev,
                          municipality: text,
                          obreb: "",
                        }));
                        setParcelMeta({
                          communeTeryt: "",
                          obrebTeryt: "",
                          teryt: "",
                        });
                      }}
                      onSelect={(o) => {
                        setFormData((prev) => ({
                          ...prev,
                          municipality: o.label,
                          obreb: "",
                        }));
                        setParcelMeta({
                          communeTeryt: o.raw.teryt,
                          obrebTeryt: "",
                          teryt: "",
                        });
                      }}
                    />
                  </div>
                  <div className="field">
                    <div style={{ display: "flex", gap: "10px" }}>
                      <img
                        src="https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_3.png"
                        className="imgLabel"
                      />
                      <span className="spanLabel">Obręb *</span>
                    </div>
                    {/* ZMIANA: obręby podpowiadane z wybranej gminy (ULDK) */}
                    <Autocomplete
                      value={formData.obreb}
                      placeholder={
                        parcelMeta.communeTeryt
                          ? "np. Śródmieście"
                          : "najpierw gmina"
                      }
                      disabled={loading}
                      options={regionOptions}
                      loading={regionsLoading}
                      minChars={0}
                      emptyText={
                        parcelMeta.communeTeryt
                          ? "Brak obrębów"
                          : "Najpierw wybierz gminę"
                      }
                      onText={(text) =>
                        setFormData((prev) => ({ ...prev, obreb: text }))
                      }
                      onSelect={(o) => {
                        setFormData((prev) => ({ ...prev, obreb: o.label }));
                        setParcelMeta((prev) => ({
                          ...prev,
                          obrebTeryt: o.raw.teryt,
                        }));
                        // ZMIANA: auto-uzupełnienie kodu pocztowego dla obrębu
                        setPostalLoading(true);
                        fetchPostalForRegion(o.raw.teryt)
                          .then((pc) => {
                            if (pc)
                              setFormData((prev) => ({
                                ...prev,
                                postalCode: pc,
                              }));
                          })
                          .finally(() => setPostalLoading(false));
                      }}
                    />
                  </div>
                  <div className="field">
                    <div style={{ display: "flex", gap: "10px" }}>
                      <img
                        src="https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_4.png"
                        className="imgLabel"
                      />
                      <span className="spanLabel">Kod pocztowy *</span>
                    </div>
                    <input
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      placeholder={postalLoading ? "szukam kodu…" : "00-000"}
                      disabled={loading}
                    />
                  </div>
                </div>

                {parcelFilled && (
                  <div className="parcel-selected-notice">
                    <svg
                      viewBox="0 0 20 20"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M10 2a8 8 0 1 1 0 16A8 8 0 0 1 10 2z" />
                      <path d="M7 10l2 2 4-4" />
                    </svg>
                    Działka <strong>{formData.parcelNo}</strong> —{" "}
                    <strong>{formData.municipality}</strong>
                    {formData.obreb ? `, obręb: ${formData.obreb}` : ""}
                    {formData.postalCode ? `, ${formData.postalCode}` : ""}
                  </div>
                )}

                <div className="map-divider">
                  <span className="map-divider__line" />
                </div>
              </div>
            )}

            {/* ════ PRZEŁĄCZNIK ════ */}
            <div className="no-parcel-bar">
              <div className="field">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={noParcel}
                    onChange={handleNoParcelChange}
                    disabled={loading}
                  />
                  Nie znam danych działki
                </label>
              </div>
              {noParcel && (
                <span className="no-parcel-hint">
                  Wypełnij dane kontaktowe i opcjonalnie dodaj załączniki.
                  Skontaktujemy się z&nbsp;Tobą.
                </span>
              )}
            </div>

            {/* ════ DANE OSOBOWE ════ */}
            <div className="input-row">
              <div className="field">
                <div style={{ display: "flex", gap: "10px" }}>
                  <img
                    src={
                      noParcel
                        ? "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_1.png"
                        : "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/5.png"
                    }
                    className="imgLabel"
                    style={{ width: "8%" }}
                  />
                  <span className="spanLabel">Imię i nazwisko *</span>
                </div>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jan Kowalski"
                  disabled={loading}
                />
              </div>
              <div className="field">
                <div style={{ display: "flex", gap: "10px" }}>
                  <img
                    src={
                      noParcel
                        ? "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_2.png"
                        : "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/6.png"
                    }
                    className="imgLabel"
                    style={{ width: "8%" }}
                  />
                  <span className="spanLabel">Adres e-mail *</span>
                </div>
                <input
                  name="mail"
                  value={formData.mail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="jan@kowalski.pl"
                  disabled={loading}
                  style={{ borderColor: errors.mail ? "red" : "" }}
                />
                {errors.mail && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {errors.mail}
                  </span>
                )}
              </div>

              <div className="field">
                <div style={{ display: "flex", gap: "10px" }}>
                  <img
                    src={
                      noParcel
                        ? "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_3.png"
                        : "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/7.png"
                    }
                    className="imgLabel"
                    style={{ width: "8%" }}
                  />
                  <span className="spanLabel">Telefon *</span>
                </div>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="123 123 123"
                  disabled={loading}
                  style={{ borderColor: errors.phone ? "red" : "" }}
                />
                {errors.phone && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {errors.phone}
                  </span>
                )}
              </div>
            </div>

            {/* ════ CENA M2 + ZAŁĄCZNIKI ════ */}
            <div style={{ display: "flex", gap: "20px" }}>
              {/* cena m2 — tylko tryb parcel */}
              {!noParcel && (
                <div style={{ width: "50%" }}>
                  <div className="input-row">
                    <div className="field">
                      <div style={{ display: "flex", gap: "10px" }}>
                        <img
                          src="https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/8.png"
                          className="imgLabel"
                          style={{ width: "5%" }}
                        />
                        <span className="spanLabel">
                          Cena 1&nbsp;m² gruntu&nbsp;(opcjonalnie)
                        </span>
                      </div>
                      <div className="input-with-suffix">
                        <input
                          name="cena_m2"
                          type="number"
                          min="1"
                          step="1"
                          value={formData.cena_m2}
                          onChange={handleChange}
                          placeholder="np. 120"
                          disabled={loading}
                        />
                        <span className="input-suffix">zł/m²</span>
                      </div>
                      <span className="span_info">
                        Jeśli znasz lokalną cenę gruntu, wpisz ją — analiza
                        będzie dokładniejsza. Pozostaw puste, aby system
                        wyznaczył cenę automatycznie.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* załączniki — zawsze widoczne, pełna szerokość w trybie contact */}
              <div style={{ width: noParcel ? "100%" : "50%" }}>
                <div className="input-row">
                  <div className="field" style={{ flex: "1 1 100%" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <img
                        src={
                          noParcel
                            ? "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/02/n_4.png"
                            : "https://odszkodowaniazaslupy.pl/wp-content/uploads/2026/03/9.png"
                        }
                        className="imgLabel"
                        style={{ width: "5%" }}
                      />
                      <span className="spanLabel">Załącznik (opcjonalnie)</span>
                    </div>

                    <div
                      className="file-drop-zone"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add("drag-over");
                      }}
                      onDragLeave={(e) =>
                        e.currentTarget.classList.remove("drag-over")
                      }
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("drag-over");
                        handleFiles({
                          target: { files: Array.from(e.dataTransfer.files) },
                        });
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Kliknij lub przeciągnij pliki</span>
                      <span className="file-drop-note">
                        Maks. 10 plików, łącznie do 20&nbsp;MB
                      </span>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={handleFiles}
                      disabled={loading}
                    />

                    {files.length > 0 && (
                      <ul className="file-list">
                        {files.map((f, i) => (
                          <li key={i} className="file-item">
                            <svg
                              viewBox="0 0 20 20"
                              width="13"
                              height="13"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              style={{ flexShrink: 0 }}
                            >
                              <path d="M13 2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7z" />
                              <polyline points="13 2 13 7 18 7" />
                            </svg>
                            <span className="file-name">{f.name}</span>
                            <span className="file-size">
                              ({(f.size / 1024).toFixed(0)} KB)
                            </span>
                            <button
                              type="button"
                              className="file-remove"
                              onClick={() => removeFile(i)}
                              aria-label="Usuń"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ════ POLITYKA ════ */}
            <div className="input-row">
              <div className="field">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={pp}
                    onChange={(e) => setPP(e.target.checked)}
                    required
                    disabled={loading}
                  />
                  Po zapoznaniu się z{" "}
                  <a
                    href="https://odszkodowaniazaslupy.pl/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    polityką prywatności
                  </a>{" "}
                  akceptuję jej postanowienia *
                </label>
              </div>
            </div>

            {/* ════ SUBMIT ════ */}
            <div>
              <button className="submit-button" disabled={loading}>
                Wyślij
              </button>
              <p className="required-note">* pole obowiązkowe</p>
            </div>
          </form>

          {error && <div className="error-msg">{error}</div>}
        </section>

        {/* ════ MODAL MAPY ════ */}
        {showMapModal && (
          <div className="modal-overlay" onClick={() => setShowMapModal(false)}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span className="modal-title">Wskaż działkę na mapie</span>
                <button
                  className="modal-close"
                  onClick={() => setShowMapModal(false)}
                  aria-label="Zamknij"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <MapParcelPicker
                  onParcelSelected={handleParcelFromMap}
                  disabled={false}
                />
              </div>
            </div>
          </div>
        )}

        {/* ════ MODAL SUKCESU ════ */}
        {showSuccess && (
          <div className="modal-overlay" onClick={() => setShowSuccess(false)}>
            <div
              className="modal-box modal-box--success"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="success-icon-wrap">
                <svg viewBox="0 0 60 60" width="60" height="60" fill="none">
                  <circle
                    cx="30"
                    cy="30"
                    r="28"
                    stroke="#7dd4d4"
                    strokeWidth="2.5"
                  />
                  <path
                    d="M18 31l9 9 15-18"
                    stroke="#7dd4d4"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="success-title">Dziękujemy za zgłoszenie!</h2>
              <p className="success-desc">
                Twoje dane zostały pomyślnie zapisane i są już u nas. Aktualnie
                je analizujemy, aby jak najlepiej odpowiedzieć na Twoje
                potrzeby. Skontaktujemy się z Tobą w ciągu najbliższych 48
                godzin.
              </p>
              <button
                className="submit-button success-close-btn"
                onClick={() => setShowSuccess(false)}
              >
                Zamknij
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default App;
